import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { uipApiInstance } from '../../services';
import { logEventToBackEnd } from '../global/globalSlice';
import { StatusVariant } from '../../components/molecules/AdminTable/Status';
import { API_VERSION, FEATURE_NAMES, STATUSVARIANTS } from '../../common/constants';
import { GoalRecommendationType } from '../../common/types';
import { setEnableConfirmConsoleNavigation } from '../../features/client';
import { checkFFEnabled, getClientJourneyFlowType } from '../../utils';
import { startLoading, stopLoading } from '../../features/global/appLoader';
import { shouldKeepPlanEditsInStore, trackUnsavedGoalChanges } from '../unsaved-plan-changes';

// Per-term EWS maps + the affectedEwsTerms dirty flag now live in the dedicated `ews` slice, not in
// adviceGoals.global. When persisting goals we still write them under global.ewsRequests/
// ewsResponses/affectedEwsTerms so the stored document keeps its legacy shape (and can be
// re-hydrated into the slice on load). ewsLoading is a transient UI flag and is not persisted.
const withReconstructedEwsGlobal = (payload: any, state: any) => ({
    ...payload,
    global: {
        ...payload?.global,
        ewsRequests: state?.ews?.requests ?? {},
        ewsResponses: state?.ews?.responses ?? {},
        affectedEwsTerms: state?.ews?.affectedEwsTerms ?? []
    }
});

const sanitizeGoalsPayload = (payload: any, options: { keepGoalId?: boolean } = {}, isFlow2: boolean = true) => {
    // goalId/accountId stripping is owned by the ID flag (AEX-3021-ID); timestamp/isNew stripping is
    // owned by the timestamps flag (AEX-3021). Each concern is gated independently.
    const idFF = checkFFEnabled(FEATURE_NAMES['AEX-3590_3604_NEW_FLOW_UPDATE_SAVE_PLAN']);
    const tsFF = checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']);
    if (idFF && !tsFF) {
        return payload;
    }
    const { keepGoalId = false } = options;
    // For non-flow2 (Advice) tenants, FE generates and sends goalId/accountId, so IDs are kept. When
    // the ID flag is off, IDs are also kept (FE still generates them).
    const keepGoalIds = idFF || keepGoalId || !isFlow2;
    const keepAccountId = idFF || !isFlow2;
    const sanitized = { ...payload };
    if (sanitized.formFields) {
        sanitized.formFields = sanitized.formFields.map((field: any) => {
            const sanitizedField = { ...field };
            if (!keepGoalIds) {
                delete sanitizedField.goalId;
            }
            if (sanitizedField.data) {
                const newData = { ...sanitizedField.data };
                if (!keepGoalIds) {
                    delete newData.goalId;
                }
                if (!keepAccountId) {
                    delete newData.accountId;
                }
                if (tsFF) {
                    delete newData.createdAt;
                    delete newData.updatedAt;
                    delete newData.isNew;
                }
                sanitizedField.data = newData;
            }
            return sanitizedField;
        });
    }
    if (tsFF && sanitized.global) {
        const { planCreationDate, lastUpdateDate, lastUpdatedDate, ...restGlobal } = sanitized.global;
        sanitized.global = restGlobal;
    }
    if (sanitized.accounts) {
        const mapAccounts = (sources: any[] = []) =>
            sources.map((src: any) => {
                const rest = { ...src };
                // Banorte (flow2): strip accountId (backend-managed). Advice (non-flow2): keep accountId.
                if (!keepAccountId) {
                    delete rest.accountId;
                }
                if (tsFF) {
                    // isNew is stripped in both tenants; createdAt/updatedAt only for non-flow2 (Advice).
                    delete rest.isNew;
                    if (!isFlow2) {
                        delete rest.createdAt;
                        delete rest.updatedAt;
                    }
                }
                return rest;
            });
        if (sanitized.accounts.incomeSources) {
            sanitized.accounts.incomeSources = mapAccounts(sanitized.accounts.incomeSources);
        }
        if (sanitized.accounts.investmentsSources) {
            sanitized.accounts.investmentsSources = mapAccounts(sanitized.accounts.investmentsSources);
        }
    }
    return sanitized;
};

export type AdviceGoalPayloadType = {
    document: any;
    goeConfigEmail: string;
    _id?: string;
    apiName: string;
    tenant: string;
    apiVer: string;
    domainEmail: string;
    assignedClientId: string;
    formFields: any[];
    request: any[];
    response: any[];
    isMultiGoal: boolean;
    iwsApplied: boolean;
    events: Array<{
        event_type: string;
        goal_id: string;
    }>;
    notifications: Array<{
        notification_id?: string;
        status?: string;
    }>;
    riskProfile?: string;
    loading: boolean;
    error: null | undefined | any;
    recommendation?: GoalRecommendationType[];
    infusionindex?: any;
    da_advice_id?: string;
    // AEX-3363: optimistic-concurrency reference id captured from mpgetproposaldetail and echoed back
    // on updateproposals / mpsave_proposal so the backend can detect a stale plan (edited in another
    // tab) before applying the update.
    proposal_ref_id?: string;
    global: {
        banner?: {
            visible: boolean | 'new';
            type?: 'attention' | 'success';
            isGoalSummary?: boolean;
            message?: string;
        };
        lastUpdatedDate?: string;
        planCreationDate?: string;
        enablePDFGenerate?: boolean;
        enablePlanDetails?: boolean;
        hasRunPipe?: boolean;
        editMode?: boolean;
        isPlanRun?: boolean;
        isPlanSavedOnce?: boolean;
        status?: StatusVariant;
        probabilityOfSuccess?: number;
        equityFixedIncome?: string;
        responseGoalCalculator?: any[];
        ewsRequests?: Record<string, Record<string, any>>;
        ewsResponses?: Record<string, Record<string, any>>;
        planExecutionResponse?: Record<string, any>;
        planExecutionStatus?: 'idle' | 'loading' | 'success' | 'error';
    };
};
const initialState: AdviceGoalPayloadType = {
    _id: '',
    apiName: '',
    tenant: '',
    apiVer: API_VERSION,
    domainEmail: '',
    assignedClientId: '',
    formFields: [],
    request: [],
    response: [],
    isMultiGoal: false,
    iwsApplied: false,
    events: [],
    notifications: [],
    riskProfile: '',
    loading: false,
    error: '',
    goeConfigEmail: '',
    da_advice_id: undefined,
    document: undefined,
    recommendation: [],
    infusionindex: {},
    global: {
        banner: {
            visible: 'new'
        },
        editMode: false,
        hasRunPipe: false,
        isPlanRun: false,
        status: 'incomplete',
        enablePDFGenerate: false,
        enablePlanDetails: false,
        responseGoalCalculator: [],
        ewsRequests: {},
        ewsResponses: {},
        planExecutionResponse: {},
        planExecutionStatus: 'idle'
    }
};

const adviceGoals = createSlice({
    name: 'adviceCreateGoal',
    initialState,
    reducers: {
        initializeGoals: (state) => ({ ...initialState, tenant: state.tenant }),
        syncAdviceGoals: (state, action: PayloadAction<any>) => {
            state = action.payload;
            return state;
        },
        showLoader: (state) => {
            state.loading = true;
        },
        removeLoader: (state) => {
            state.loading = false;
        },
        updateBanner: (state, action: PayloadAction<any>) => {
            state.global.banner = action.payload;
        },
        updateStatus: (state, action: PayloadAction<StatusVariant>) => {
            state.global.status = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(createGoal.fulfilled, (state, action) => {
            const response = action.payload;
            // eslint-disable-next-line no-unused-vars
            const { __meta, ...rest } = response.document ? response.document : response;
            state._id = rest._id;
            state.apiName = rest.api_name;
            state.response = rest.response;
            state.request = rest.request;
            state.formFields = rest.form_fields;
            state.error = '';
            state.assignedClientId = rest.assignedClientId;
            state.domainEmail = rest.domainEmail;
            state.apiVer = rest.api_ver;
            state.isMultiGoal = rest.is_multi_goal;
            state.iwsApplied = rest.iws_applied;
            state.riskProfile = rest.risk_profile;
            state.global = rest.global;
        }),
            builder.addCase(getPlanExecution.pending, (state) => {
                state.global.planExecutionResponse = {};
                state.global.planExecutionStatus = 'loading';
            }),
            builder.addCase(getPlanExecution.fulfilled, (state, action) => {
                // Only treat the response as a success when it contains a usable rebalancerResult.
                // A non-empty error/timeout payload without rebalancerResult must fall through to the error state.
                const rebalancerResult = action.payload?.rebalancerResult;
                if (rebalancerResult && Object.keys(rebalancerResult).length > 0) {
                    state.global.planExecutionResponse = action.payload;
                    state.global.planExecutionStatus = 'success';
                } else {
                    state.global.planExecutionResponse = {};
                    state.global.planExecutionStatus = 'error';
                }
            }),
            builder.addCase(getPlanExecution.rejected, (state) => {
                state.global.planExecutionResponse = {};
                state.global.planExecutionStatus = 'error';
            });
    }
});

export const createGoal = createAsyncThunk(
    'advice/creategoals',
    async (data: any, { dispatch, rejectWithValue, getState }: any) => {
        try {
            dispatch(showLoader());
            const state = getState();
            const adviceClient = state.adviceClient;
            const adviceGoals = state.adviceGoals;
            const isFlow2 = getClientJourneyFlowType(adviceClient?.tenant) === 'flow2';
            if (!adviceGoals?.global?.isPlanRun) {
                const response = await uipApiInstance({
                    method: 'POST',
                    url: '/api/advice/createproposals',
                    withCredentials: false,
                    data: sanitizeGoalsPayload(withReconstructedEwsGlobal(data, state), {}, isFlow2)
                });
                dispatch(logEventToBackEnd('GOAL_CREATION_SUCCESS'));
                if (response?.data?.document) {
                    const doc = response?.data?.document;
                    let recommendationArr: any[] = [];
                    doc.form_fields.map((goal: any) => {
                        recommendationArr.push({
                            goalId: goal.goalId,
                            oneTimeTopUp: 0,
                            topUpAccumulation: 0,
                            topUpDecumulation: 0,
                            recomendedTenure: 0
                        });
                    });
                    const transformedData = {
                        _id: doc._id,
                        apiName: doc.api_name,
                        tenant: doc.tenant,
                        apiVer: doc.api_ver,
                        assignedClientId: doc.assignedClientId,
                        formFields: doc.form_fields,
                        request: doc.request,
                        response: doc.response,
                        recommendation: recommendationArr,
                        isMultiGoal: doc.is_multi_goal,
                        iwsApplied: doc.iws_applied,
                        da_advice_id: doc.da_advice_id,
                        global: doc.global,
                        loading: false,
                        error: '',
                        goeConfigEmail: doc.goeConfigEmail
                    };
                    dispatch(syncAdviceGoals(transformedData));
                }
                return response?.data?.document;
            } else {
                dispatch(syncAdviceGoals(data));
                if (!adviceClient?.enableConfirmConsoleNavigation) dispatch(setEnableConfirmConsoleNavigation(true));
            }
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('GOAL_CREATION__ERROR'));
            return rejectWithValue(err);
        } finally {
            dispatch(stopLoading());
        }
    }
);

export const saveGoal = (goalsData: any) => {
    return async (dispatch: any, getState: any) => {
        const state = getState();
        const isFlow2 = getClientJourneyFlowType(state?.auth?.user?.tenant) === 'flow2';
        if (!shouldKeepPlanEditsInStore(state, goalsData.global)) {
            return dispatch(updateGoal(goalsData)).unwrap();
            //unwrap success -returns actual payload, fail -throws error so can catch
        } else {
            if (isFlow2) {
                dispatch(trackUnsavedGoalChanges(goalsData.formFields));
            }
            dispatch(
                syncAdviceGoals({
                    ...goalsData,
                    global: {
                        ...goalsData.global,
                        status: STATUSVARIANTS.INCOMPLETE
                    }
                })
            );
            dispatch(setEnableConfirmConsoleNavigation(true));
        }
    };
};

export const updateGoal = createAsyncThunk(
    'advice/updateproposals',
    async (data: any, { dispatch, rejectWithValue, getState }: any) => {
        const state = getState();
        const rebalancer_ref_id = state?.adviceGoals?.global?.planExecutionResponse?.rebalancer_ref_id;
        const isFlow2 = getClientJourneyFlowType(state?.adviceClient?.tenant) === 'flow2';
        // Avoid mutating `data` — it may be a frozen Redux state object after syncAdviceGoals.
        const { error: _omitError, ...payload } = data ?? {};
        try {
            dispatch(startLoading());
            const sanitizedData = sanitizeGoalsPayload(
                withReconstructedEwsGlobal(payload, state),
                { keepGoalId: true },
                isFlow2
            );
            // AEX-3363: echo proposal_ref_id (flow2 + FF only) so the backend can reject a stale save.
            const proposalRefId = state?.adviceGoals?.proposal_ref_id;
            const stalePlanEnabled = checkFFEnabled(FEATURE_NAMES['AEX-3342_ADD_VALIDATION_BEFORE_UPDATE']);
            const goalPayload = {
                ...sanitizedData,
                ...(rebalancer_ref_id ? { rebalancer_ref_id } : {}),
                ...(stalePlanEnabled && isFlow2 && proposalRefId ? { proposal_ref_id: proposalRefId } : {})
            };
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/updateproposals',
                withCredentials: false,
                data: goalPayload
            });
            dispatch(logEventToBackEnd('GOAL_UPDATE_SUCCESS'));

            if (response?.data?.document) {
                const doc = response?.data?.document;
                let recommendationArr: GoalRecommendationType[] = [];
                doc.form_fields.map((goal: any) => {
                    recommendationArr.push({
                        goalId: goal.goalId,
                        oneTimeTopUp: 0,
                        topUpAccumulation: 0,
                        topUpDecumulation: 0,
                        recomendedTenure: 0
                    });
                });
                const transformedData = {
                    _id: doc._id,
                    apiName: doc.api_name,
                    tenant: doc.tenant,
                    apiVer: doc.api_ver,
                    assignedClientId: doc.assignedClientId,
                    formFields: doc.form_fields,
                    request: doc.request ?? [],
                    response: doc.response ?? [],
                    isMultiGoal: doc.is_multi_goal,
                    iwsApplied: doc.iws_applied,
                    loading: false,
                    error: '',
                    goeConfigEmail: doc.goeConfigEmail,
                    // AEX-3363: keep the reference id so the next update echoes the freshest value.
                    proposal_ref_id: doc.proposal_ref_id ?? proposalRefId,
                    recommendation: recommendationArr,
                    global: doc.global
                };
                dispatch(syncAdviceGoals(transformedData));
            }
            return response?.data?.document;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('GOAL_CREATION__ERROR'));
            return rejectWithValue(err);
        } finally {
            dispatch(stopLoading());
            // dispatch(setEnableConfirmConsoleNavigation(false));
        }
    }
);

export const mpSaveProposal = async (data: any) => {
    const sanitizedData = checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])
        ? (() => {
              const sanitized = { ...data };
              if (sanitized.proposals?.global) {
                  const { planCreationDate, lastUpdateDate, lastUpdatedDate, ...restGlobal } =
                      sanitized.proposals.global;
                  sanitized.proposals = { ...sanitized.proposals, global: restGlobal };
              }
              return sanitized;
          })()
        : data;
    const response = await uipApiInstance({
        method: 'POST',
        url: '/api/advice/mpsave_proposal',
        withCredentials: false,
        data: sanitizedData
    });
    return response?.data;
};

export const getGoalCategory = async (data: {
    language: string;
    goals: {
        goalName: string;
        goalTargetDate: string;
        goalStartDate?: string;
    }[];
}) => {
    const response = await uipApiInstance({
        method: 'POST',
        url: '/api/advice/generategoalcategory',
        withCredentials: false,
        data
    });
    return response?.data;
};

export const getPlanExecution = createAsyncThunk(
    'advice/getplanexecution',
    async (
        data: {
            accounts: {
                incomeSources: Record<string, any>[] | null;
                investmentsSources: Record<string, any>[] | null;
            };
            ewsResponses: {
                longTerm: {
                    ews: Record<string, any>[];
                    runPipeResponses: Record<string, any>[];
                } | null;
                mediumTerm: {
                    ews: Record<string, any>[];
                    runPipeResponses: Record<string, any>[];
                } | null;
                shortTerm: {
                    ews: Record<string, any>[];
                    runPipeResponses: Record<string, any>[];
                } | null;
            };
        },
        { rejectWithValue }
    ) => {
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/rebalancer',
                withCredentials: false,
                data
            });
            return response?.data;
        } catch (err) {
            console.log(err);
            return rejectWithValue(err);
        }
    }
);

// export const getPlanExecution = async (data: {
//     accounts: {
//         incomeSources: Record<string, any>[] | null;
//         investmentsSources: Record<string, any>[] | null;
//     },
//     ewsResponses: {
//         longTerm: {
//             ews: Record<string, any>[];
//             runPipeResponses: Record<string, any>[];
//         } | null;
//         mediumTerm: {
//             ews: Record<string, any>[];
//             runPipeResponses: Record<string, any>[];
//         } | null;
//         shortTerm: {
//             ews: Record<string, any>[];
//             runPipeResponses: Record<string, any>[];
//         } | null;
//     };
// }) => {
//     const response = await uipApiInstance({
//         method: 'POST',
//         url: '/api/advice/getplanexecution',
//         withCredentials: false,
//         data
//     });
//     return response?.data;
// }

export const { initializeGoals, syncAdviceGoals, showLoader, removeLoader, updateStatus, updateBanner } =
    adviceGoals.actions;

export default adviceGoals.reducer;
